import { Endpoint } from '@business/api/endpoint';
import { FieldMapping, FieldMappingOverlayType, ReportDataRequest, ReportDataType } from '@business/entities/dynamicReprots';
import { ReportCategoryId } from '@business/entities/ReportCategory';
import { PermissionService } from '@business/services/permission_service';
import { ReportService } from '@business/services/report_service';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useHistory } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLazyEffect } from '@core/utils/react';
import { CBody } from '@modules/common/CBody';
import { ReportBuilderFormData } from '@modules/dynamicReports/ReportForms';
import { openOrderOverlay } from '@modules/orders/OrderOverlay';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { IDataTableColumn } from 'react-data-table-component-footer';
import { openDetailsReportOverlay } from '..';
import { ReportFilterFormType, ReportFilters } from './ReportFilters';
import { ReportSummary } from './ReportSummary';
import { ReportViewerBloc } from './ReportViewerBloc';
import { openOrganizationOverlay } from '@modules/organizations/OrganizationOverlay';
import { openDealOverlay } from '@modules/deals/DealOverlay';
import { openProjectOverlay } from '@modules/projects/ProjectOverlay';
import { TableColumn } from 'react-data-table-component';
import { OrgId } from '@business/entities/organization';
import { openViewProposal } from '@modules/proposals/ViewProposalModal';
import { openTimeMaterialOverlay } from '@modules/timeMaterials/TimeMaterialOverlay';

function toRequest(data: ReportBuilderFormData): ReportDataRequest {
  return {
    source: data.source.view,
    groupBy: data.groupBy,
    sortBy: data.sortBy,
    sortDirection: data.sortDirection,
    columns: data.columns.map(c => ({
      name: c.key,
      label: c.label,
      groupBy: c.groupBy,
      menuOrder: c.menuOrder,
      showTotal: !!c.showTotal,
      totalFormula: c.totalFormula,
    })),
    filters: data.filters.map(c => ({
      name: c.key,
      operator: c.operator,
      value: c.value,
    })),
    dateColumn: data.dateColumn,
    snapshotDateColumn: data.snapshotDateColumn,
    summary: data.summary,
  };
}

export const renderReportColumn = (row: ReportDataType, key: string, mapping: FieldMapping, raw = false) => {
  const value = ReportService.renderReportCell(row, key, mapping, raw);
  const rawValue = R.toString(value);
  const onPress = () => {
    if (mapping.link === 'organization') {
      const orgId = R.toString(row['link_organization_id']);
      openOrganizationOverlay({ orgId });
    } else if (mapping.link === 'order') {
      const orderId = R.toString(row['link_order_id']);
      openOrderOverlay({ orderId });
    } else if (mapping.link === 'deal') {
      const dealId = R.toString(row['link_deal_id']);
      openDealOverlay({ dealId, orgId: R.toString(row['link_organization_id'] || '0') });
    } else if (mapping.link === 'project') {
      const projectId = R.toString(row['link_project_id']);
      openProjectOverlay({ projectId, projectTitle: '' });
    } else if (mapping.link === 'proposal') {
      const proposalId = R.toString(row['link_proposal_id']);
      openViewProposal({ proposalId });
    } else if (mapping.link === 'tm') {
      const orderId = R.toString(row['link_tm_id']);
      openTimeMaterialOverlay({ orderId: orderId, boardId: '0' });
    } else if (mapping.link === 'ticket') {
      const ticketId = R.toString(row['link_ticket_id']);
      window.open(`/support-tickets/detail/${ticketId}`);
      //   openProjectOverlay({ projectId, projectTitle: '' });
    }
  };

  if (mapping?.overlay?.type && mapping.overlay?.field) {
    const overlayId = row[mapping.overlay.field];
    if (overlayId && mapping?.overlay.type == FieldMappingOverlayType.order) {
      return (
        <span className="d-flex flex-column">
          <a
            className="text-primary"
            onClick={() =>
              openOrderOverlay({
                orderId: R.toString(overlayId),
              })
            }
          >
            <span>{rawValue}</span>
          </a>
        </span>
      );
    }
  }

  if (mapping && !R.isEmpty(mapping.link)) {
    return (
      <a className="text-primary" onClick={onPress}>
        {rawValue}
      </a>
    );
  }

  return rawValue;
};

export interface ReportViewerProps {
  report: ReportBuilderFormData;
  builder?: boolean;
  filters?: boolean;
  form: ReportFilterFormType;
  categoryTitle?: string;
  categoryId?: ReportCategoryId;
  reportId?: string;
  initialQuery?: any;
  hideDownload?: boolean;
  print?: boolean;
  widget?: boolean;
  widgetId?: string;
}

export const ReportViewer = memo((props: ReportViewerProps) => {
  const {
    builder = false,
    filters = false,
    form,
    categoryTitle,
    categoryId,
    reportId,
    initialQuery,
    hideDownload = false,
    print = false,
    widget = false,
  } = props;

  const [report, setReport] = useState(props.report);
  const bloc = useBloc(ReportViewerBloc);
  const data = useObservable(bloc.data$, []);
  const additional = useObservable(bloc.additional$);
  const formValue = useNonNilObservable(form.value$);
  const history = useHistory();
  const [isSetInitialQuery, setIsSetInitialQuery] = useState(true);

  const summaryResults = useMemo(() => (additional ? additional.summary : []), [additional]);

  useEffect(() => {
    if (report) {
      bloc.paginated.patchQuery({ sortBy: '', sortDirection: 'asc' });
    }
  }, [bloc.paginated, report]);

  useEffect(() => {
    if (!isSetInitialQuery) {
      form.patchValue(initialQuery as any);
      if (R.get(initialQuery, 'sortDirection') && R.get(initialQuery, 'sortBy')) {
        bloc.paginated.patchQuery({ sortBy: initialQuery.sortBy, sortDirection: initialQuery.sortDirection });
      }
      setIsSetInitialQuery(true);
    }
  }, [bloc.paginated, form, initialQuery, isSetInitialQuery]);

  useLazyEffect(
    () => {
      if (report.source && isSetInitialQuery) {
        bloc.paginated
          .patchQuery({
            request: toRequest(report),
            dateRange: formValue.dateRange,
            filters: R.objectCompact(formValue.filters),
            page: 1,
            search: formValue.search,
            widgetId: props.widgetId,
          })
          .subscribe();
      }
    },
    [bloc, report, formValue, isSetInitialQuery],
    500,
  );

  useLazyEffect(
    () => {
      setReport(props.report);
    },
    [props.report],
    1000,
  );

  useEffect(() => {
    if (!builder) {
      const params = R.omitBy(R.omit(bloc.paginated.pageQuery$.value, 'request'), R.isNil);
      const rawQuery = {
        reportId,
        categoryId,
        params,
      };
      const query = encodeURIComponent(btoa(JSON.stringify(rawQuery)));
      history.push(`/reports-2/${R.slug(categoryTitle || 'uncategorized')}/${R.slug(report.name)}${Endpoint._getQueryString({ q: query })}`);
    }
  }, [bloc.paginated.pageQuery$.value, builder, categoryId, categoryTitle, history, report.name, reportId]);

  //   useEffect(() => {
  //     // bloc.paginated.patchQuery({ dateRange: formValue.dateRange, filters: formValue.filters }).subscribe();
  //   }, [bloc, formValue]);

  const columns: GCColumn<ReportDataType>[] = useMemo(() => {
    if (!report.source) return [];
    const columns = report.columns.map(item => {
      return {
        name: item.label,
        sortField: item.label,
        sortable: true,
        cell: row => renderReportColumn(row, item.label, report.source.mapping[item.key], report.source.isGraph ? true : false),
      } as GCColumn<ReportDataType>;
    });

    if (report.groupBy && report.detailedColumns.length) {
      columns.push({
        name: '',
        sortField: 'detail',
        sortable: false,
        width: '75px',
        cell: row =>
          row['isFooter'] ? null : (
            <a onClick={() => openDetailsReportOverlay({ report, detailFilter: row['rowGroupByValue']!, viewerForm: formValue })}>
              <i className="fas fa-eye"></i>
            </a>
          ),
      } as GCColumn<ReportDataType>);
    }
    return columns;
  }, [formValue, report]);

  const onSort = useCallback(
    (column: TableColumn<ReportDataType>, sortDirection: 'asc' | 'desc') => {
      bloc.paginated.patchQuery({ sortBy: column.sortField!, sortDirection }).subscribe();
    },
    [bloc.paginated],
  );

  const hasFilters = useMemo(
    () => report.reportFilters.length > 0 || !!report.dateColumn || !!report.search,
    [report.dateColumn, report.reportFilters.length, report.search],
  );

  const footer = useMemo(() => {
    if (additional && additional.totals && Object.keys(additional.totals).length > 0) {
      return additional.totals!;
    }
    return null;
  }, [additional]);

  const Wrapper = print || widget ? Fragment : CBody;
  return (
    <Fragment>
      {filters && hasFilters && <ReportFilters report={report} form={form} builder={builder} />}
      <ReportSummary report={report} results={summaryResults} />
      <Wrapper>
        {!hideDownload && PermissionService.canExportData() && (
          <div style={{ justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem', display: 'flex', flex: 1 }}>
            <a onClick={() => bloc.downloadCSV(report.name)}>
              <i className="bx bx-cloud-download mr-2"></i>
              Download CSV
            </a>
          </div>
        )}
        <GCTable
          noSearch
          sortServer
          striped={!print}
          bordered={!(print || widget)}
          footer={footer}
          onSort={onSort}
          noHeader
          columns={columns}
          data={data}
          paginated={bloc.paginated}
          paginationPerPage={print ? 50 : 10}
          pagination={!print}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
          noQueryOnSearch
        />
      </Wrapper>
    </Fragment>
  );
});
